import React from "react";
import { Button } from "@heroui/react";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { StepDefinition, FieldDefinition } from "../../../../types/types";
import {
  humanizeLabel,
  buildItemDefaults,
  buildOptions,
  renderSingleField,
} from "../dealFormFields";
import { FieldHelpers, FormValue } from "../dealFormTypes";
import { TranslationFn } from "../dealFormFields";

export type RepeatableStepProps = {
  step: StepDefinition;
  stepId: string;
  formData: Record<string, FormValue>;
  fieldHelpers: FieldHelpers;
  t: TranslationFn;
  updateFormAtPath: (path: string[], value: FormValue) => void;
};

export const RepeatableStep = ({
  step,
  stepId,
  formData,
  fieldHelpers,
  t,
  updateFormAtPath,
}: RepeatableStepProps): React.ReactElement | null => {
  const fields = step.fields ?? [];
  const items: Array<Record<string, FormValue>> = Array.isArray(formData?.[stepId])
    ? (formData[stepId] as Array<Record<string, FormValue>>)
    : [];
  const itemDefaults = step.templateItemDefaults ?? buildItemDefaults(fields);

  const setItems = (next: Array<Record<string, FormValue>>) =>
    updateFormAtPath([stepId], next);

  const buildOptionsLocal = (
    f: FieldDefinition,
    contextData?: Record<string, FormValue>,
  ) => buildOptions(f, contextData, formData, fieldHelpers.optionsBySource);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-[14px] font-semibold text-slate-600">
            {humanizeLabel(step.title || t("components"))}
          </h3>
          {items.length > 0 && (
            <p className="text-[12px] text-slate-400 mt-0.5">
              {t("itemsConfigured", { count: items.length })}
            </p>
          )}
        </div>
        <Button
          color="primary"
          size="sm"
          radius="lg"
          onPress={() => setItems([...items, { ...(itemDefaults || {}) }])}
          startContent={<PlusIcon className="w-3.5 h-3.5" />}
        >
          {t("addComponent")}
        </Button>
      </div>

      {items.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 border-2 border-dashed border-slate-100 rounded-xl bg-slate-50/40">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
            <PlusIcon className="w-5 h-5 text-slate-300" />
          </div>
          <p className="text-[13px] font-semibold text-slate-400">
            {t("noItemsAddedYet")}
          </p>
          <p className="text-[12px] text-slate-300 mt-0.5">
            {t("clickAddComponentToStart")}
          </p>
        </div>
      )}

      <div className="space-y-4">
        {items.map((item: Record<string, FormValue>, index: number) => {
          const updateItem = (key: string, val: FormValue) => {
            if (key === "__patch__" && val && typeof val === "object") {
              updateFormAtPath(
                [stepId],
                items.map((row, i) =>
                  i === index
                    ? { ...row, ...(val as Record<string, FormValue>) }
                    : row,
                ),
              );
            } else {
              updateFormAtPath(
                [stepId],
                items.map((row, i) =>
                  i === index ? { ...row, [key]: val } : row,
                ),
              );
            }
          };

          const compTypeField = fields.find((f) => f.key === "componentType");
          const compTypeLabel = compTypeField
            ? (buildOptionsLocal(compTypeField, item).find(
                (o: { key: string; label: string }) =>
                  o.key === item.componentType,
              )?.label ?? humanizeLabel(item.componentType as string | undefined))
            : (item.componentType as string | undefined);
          const helpersForItem = {
            ...fieldHelpers,
            repeatableItems: items,
            repeatableIndex: index,
          };

          return (
            <div
              key={index}
              className="border border-slate-200 rounded-xl bg-white overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-100">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-lg bg-blue-600 text-white text-[11px] font-bold">
                    {index + 1}
                  </span>
                  {compTypeLabel && (
                    <span className="text-[13px] font-semibold text-slate-700">
                      {compTypeLabel}
                    </span>
                  )}
                  {item.mandatory === true && (
                    <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[11px] font-semibold border border-amber-200">
                      {t("mandatory")}
                    </span>
                  )}
                </div>
                <Button
                  color="danger"
                  variant="light"
                  size="sm"
                  radius="lg"
                  onPress={() => setItems(items.filter((_, i) => i !== index))}
                  startContent={<TrashIcon className="w-3.5 h-3.5" />}
                >
                  {t("remove")}
                </Button>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4 items-start">
                  {fields.map((f) =>
                    renderSingleField(
                      stepId,
                      f,
                      (formData[stepId] ?? {}) as Record<string, FormValue>,
                      item,
                      updateItem,
                      helpersForItem,
                    ),
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
